// zr-webrtc — the media engine. Owns RTCPeerConnections, ICE config, the P2P vs SFU
// path decision, and the WHIP/WHEP broadcast clients. Platform-specific bits
// (how to make a peer connection / grab media) are injected via PlatformAdapter.

import { classifyQuality, MAX_MATCHMAKER_PARTICIPANTS, MAX_SFU_PARTICIPANTS, PROVIDER_LABELS, extractPeerTransport, isAlive, clampKeepAlive, extractStatsSample, computeQualityDetails, QualitySmoother, qualityLevelForScore, FrameCryptor, importSharedKeyBytes, deriveIdentityKey, isE2eeSupported } from 'zr-core';
import type { StatsSample } from 'zr-core';
import type { Room, SignalingClient, EventBus, QualityDetails, SignalingProvider, IceConnectionState, PeerHeartbeat, VideoEffect } from 'zr-core';
import {
  ConnectionQuality as LiveKitConnectionQuality,
  Room as LiveKitRoom,
  RoomEvent as LiveKitRoomEvent,
  Track as LiveKitTrack,
} from 'livekit-client';
import type {
  Participant as LiveKitParticipant,
  RemoteParticipant as LiveKitRemoteParticipant,
  RemoteTrack as LiveKitRemoteTrack,
  RemoteTrackPublication as LiveKitRemoteTrackPublication,
} from 'livekit-client';

// Re-export the video-effect descriptor helpers so platform adapters (e.g. adapters/web)
// can build their effect pipelines from their already-declared `zr-webrtc` dependency,
// without taking a separate bare `zr-core` import that may not resolve in every bundler.
export { cssFilterFor, normalizeEffect, isNoOpEffect, needsSegmenter, VIDEO_EFFECT_PRESETS } from 'zr-core';
export type { VideoEffect, VideoEffectType } from 'zr-core';
// Re-export the call-UX spec + reaction set so the web UI kit (adapters/web/callUI) can build
// from its already-declared `zr-webrtc` dependency without a bare `zr-core` import.
export { callUxSpec, resolveCallUx, formatCallDuration, CONTROL_SETS, INCOMING_CONTROLS, REACTION_EMOJIS } from 'zr-core';
export type { CallUxStyle, CallUxChoice, CallLayout, CallControlSpec, CallUxSpec, ReactionEmoji } from 'zr-core';

/** A live processed video track (camera + effect) with a teardown hook. */
export interface VideoEffectPipeline {
  track: MediaStreamTrack;
  stop(): void;
}

// ---------------------------------------------------------------------------
// End-to-end media encryption context (P2P path). Seals each encoded frame with
// AES-GCM via Insertable Streams so the relay only ever sees ciphertext. Each peer
// encrypts with a key derived from its OWN id (deriveIdentityKey) and decrypts a
// remote peer's frames with a key derived from THAT peer's id — distinct keystreams,
// no IV reuse. A no-op on runtimes without createEncodedStreams.
// ---------------------------------------------------------------------------
export class E2eeContext {
  private base?: Promise<Uint8Array>;
  private sendCryptor?: Promise<FrameCryptor>;
  private recvCryptors = new Map<string, Promise<FrameCryptor>>();

  constructor(private secret: Uint8Array | string, private selfId: string) {}

  static supported(): boolean {
    return isE2eeSupported();
  }

  private baseKey(): Promise<Uint8Array> {
    if (!this.base) this.base = importSharedKeyBytes(this.secret);
    return this.base;
  }

  private cryptorFor(identity: string): Promise<FrameCryptor> {
    return this.baseKey()
      .then((b) => deriveIdentityKey(b, identity))
      .then((k) => FrameCryptor.create(k));
  }

  /** Encrypt every outgoing frame on this sender (own-identity key). */
  attachSender(sender: RTCRtpSender): void {
    if (!sender || typeof (sender as any).createEncodedStreams !== 'function') return;
    if (!this.sendCryptor) this.sendCryptor = this.cryptorFor(this.selfId);
    const cryptorP = this.sendCryptor;
    try {
      const { readable, writable } = (sender as any).createEncodedStreams();
      const transform = new TransformStream({
        transform: async (frame: any, controller: any) => {
          try {
            const cryptor = await cryptorP;
            frame.data = await cryptor.encrypt(frame.data);
          } catch (err) {
            console.warn('E2EE: encrypt failed, dropping frame:', err);
            return;
          }
          controller.enqueue(frame);
        },
      });
      readable.pipeThrough(transform).pipeTo(writable);
    } catch (err) {
      console.warn('E2EE: could not attach sender transform:', err);
    }
  }

  /** Decrypt every incoming frame on this receiver (remote-peer-identity key). */
  attachReceiver(receiver: RTCRtpReceiver, peerId: string): void {
    if (!receiver || typeof (receiver as any).createEncodedStreams !== 'function') return;
    let cryptorP = this.recvCryptors.get(peerId);
    if (!cryptorP) {
      cryptorP = this.cryptorFor(peerId);
      this.recvCryptors.set(peerId, cryptorP);
    }
    try {
      const { readable, writable } = (receiver as any).createEncodedStreams();
      const transform = new TransformStream({
        transform: async (frame: any, controller: any) => {
          try {
            const cryptor = await cryptorP!;
            frame.data = await cryptor.decrypt(frame.data);
          } catch (err) {
            // A frame we can't authenticate is dropped, not surfaced — fail closed.
            return;
          }
          controller.enqueue(frame);
        },
      });
      readable.pipeThrough(transform).pipeTo(writable);
    } catch (err) {
      console.warn('E2EE: could not attach receiver transform:', err);
    }
  }
}

export interface IceConfig {
  iceServers: RTCIceServer[];
  turnApiBase?: string;
  turnToken?: string;
  turnUsername?: string;
  /** Epoch ms the active TURN credentials expire (from /v1/credentials `expires`). Drives
   *  proactive, pre-expiry rotation so credentials roll over with no media interruption. */
  turnExpiresAt?: number;
}

/** Options for the circular presenter-overlay composited onto a screen share. */
export interface ScreenCompositeOptions {
  /** Output frame rate of the composited canvas stream (default 24). */
  fps?: number;
  /** Self-cam circle diameter as a fraction of canvas height (default 0.22). */
  diameterRatio?: number;
  /** Inset from the canvas edges in px (default ~4% of height). */
  margin?: number;
  /** Which corner the bubble sits in (default 'bottom-right'). */
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  /** Ring color drawn around the bubble (default '#ffffff'). */
  ringColor?: string;
  /** Ring width in px (default scales with diameter). */
  ringWidth?: number;
}

/** A live composited stream (screen + circular self-cam) with a teardown hook. */
export interface ScreenComposite {
  stream: MediaStream;
  stop(): void;
}

/**
 * Per-connection options the engine passes when creating a PeerConnection.
 * CRITICAL: `encodedInsertableStreams` must ONLY be enabled when the engine will actually
 * attach an encoded-transform (i.e. E2EE is active). Enabling it without pumping the encoded
 * streams makes Chrome/Edge withhold ALL media frames — a silent "connected but no media" bug.
 */
export interface PeerConnectionOptions {
  encodedInsertableStreams?: boolean;
  /** ICE transport policy — 'all' (direct+relay, prefers UDP) or 'relay' (force TURN). */
  iceTransportPolicy?: 'all' | 'relay';
}

/**
 * Abstracts the few platform-specific calls so the same engine runs in the browser
 * and in React Native (react-native-webrtc). Implemented by adapters/web + adapters/mobile.
 */
export interface PlatformAdapter {
  createPeerConnection(config: IceConfig, remoteId?: string, selfId?: string, opts?: PeerConnectionOptions): RTCPeerConnection;
  getUserMedia(constraints: { audio: boolean; video: boolean }): Promise<MediaStream>;
  getDisplayMedia?(): Promise<MediaStream>;
  /**
   * Constructs an empty or track-seeded MediaStream. Routed through the adapter so the
   * engine never references the DOM `MediaStream` global directly — required for native
   * runtimes (Connected-TV / eLinux via libwebrtc/GStreamer) that have no DOM. Adapters
   * on DOM platforms simply return `new MediaStream(tracks)`.
   */
  createMediaStream?(tracks?: MediaStreamTrack[]): MediaStream;
  /**
   * Composites a circular self-cam bubble onto the screen share, returning a single
   * combined video stream to publish. Optional: when an adapter doesn't provide it
   * (e.g. mobile), the engine falls back to publishing the raw screen track.
   */
  createScreenComposite?(
    screen: MediaStream,
    camera: MediaStream | null,
    opts?: ScreenCompositeOptions
  ): ScreenComposite;
  /**
   * Process a camera track through a video effect (blur/filter/background), returning a
   * new track to publish. Optional: adapters without it (e.g. native runtimes) simply
   * don't offer effects and the engine keeps the raw camera track.
   */
  createVideoEffectPipeline?(source: MediaStreamTrack, effect: VideoEffect): VideoEffectPipeline;
}

export interface Transport {
  readonly kind: 'p2p' | 'sfu';
  readonly remoteStream?: MediaStream;
  connect(room: Room): Promise<void>;
  publish(stream: MediaStream): Promise<void>;
  replaceVideoTrack?(track: MediaStreamTrack | null): Promise<void>;
  /**
   * Pause/resume rendering of a remote peer's video (Google Meet off-screen
   * optimization). Disables the received video track so an off-screen / thumbnailed
   * tile stops consuming decode CPU; re-enable when it scrolls back into view.
   */
  setRemoteVideoEnabled?(participantId: string, enabled: boolean): void;
  /** Per-participant received stream (preferred over the legacy aggregate remoteStream). */
  getParticipantStream?(participantId: string): MediaStream | undefined;
  /**
   * Hot-swap the ICE configuration (e.g. after TURN credentials are re-minted on expiry)
   * onto every live PeerConnection and renegotiate — keeps the session alive without a
   * full teardown. Called by the resilience layer when credentials go invalid.
   */
  updateIceConfig?(ice: IceConfig, restartIce?: boolean): Promise<void>;
  close(announceLeave?: boolean): Promise<void>;
}

// ---------------------------------------------------------------------------
// TURN liveness — the engine's in-memory keep-alive against the authenticated
// ZeroRate CoTURN service (/v1/peers/ping). Shared by both transports.
// ---------------------------------------------------------------------------

/** Normalized result of a `/v1/peers/ping` liveness probe. */
export interface TurnLiveness {
  alive: boolean;
  turnReachable: boolean;
  credentialsValid: boolean;
  expiresAt?: string;
}

export interface TurnLivenessConfig {
  turnApiBase: string;
  turnToken: string;
  turnUsername: string;
}

export type LivenessFetch = (url: string, init: any) => Promise<{ ok: boolean; json(): Promise<any> }>;

/**
 * Probes CoTURN `/v1/peers/ping` on each keep-alive tick to maintain the local peer's
 * TURN connectivity inside the engine's state machine. It:
 *   • emits `turn-liveness-changed` ONLY on a state transition (no per-tick event spam),
 *   • escalates an ICE restart (via `onEscalate`) when TURN is unreachable but the
 *     credentials are still valid — gated by a cooldown so it never restart-storms, and
 *   • leaves credential-expiry recovery to the caller (surfaced via the event), because a
 *     restart cannot fix expired credentials — they must be re-minted.
 */
export class TurnLivenessMonitor {
  private last?: TurnLiveness;
  private lastEscalateTs = 0;
  private fetchImpl: LivenessFetch;
  // The credential expiry we've already signalled a rotation for — so we fire `onRotateNeeded`
  // exactly once per credential epoch (not every 3–5s tick inside the rotation window).
  private rotationSignalledFor?: string;

  constructor(
    private opts: {
      events?: EventBus;
      onEscalate?: () => void;
      /** Called once when the active TURN credentials are within `rotateBeforeMs` of expiry,
       *  so the caller can pre-emptively re-mint and hot-swap them with no media blip. */
      onRotateNeeded?: (expiresAt: string) => void;
      cooldownMs?: number;
      /** Lead time before expiry to trigger proactive rotation (default 90s). */
      rotateBeforeMs?: number;
      fetch?: LivenessFetch;
    } = {},
  ) {
    this.fetchImpl = opts.fetch ?? ((globalThis as any).fetch as LivenessFetch);
  }

  private same(a: TurnLiveness, b: TurnLiveness): boolean {
    return a.alive === b.alive && a.turnReachable === b.turnReachable && a.credentialsValid === b.credentialsValid;
  }

  /** Returns the normalized liveness, or null when the probe itself failed (retried next tick). */
  async tick(cfg: TurnLivenessConfig, now: number): Promise<TurnLiveness | null> {
    let data: any;
    try {
      const res = await this.fetchImpl(`${cfg.turnApiBase}/v1/peers/ping`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${cfg.turnToken}` },
        body: JSON.stringify({ username: cfg.turnUsername }),
      });
      if (!res.ok) return null;
      data = await res.json();
    } catch {
      return null;
    }

    const norm: TurnLiveness = {
      alive: !!data.alive,
      turnReachable: !!data.turn_reachable,
      credentialsValid: !!data.credentials_valid,
      expiresAt: data.credentials_expires_at,
    };

    if (!this.last || !this.same(this.last, norm)) {
      this.opts.events?.emit({
        type: 'turn-liveness-changed',
        alive: norm.alive,
        turnReachable: norm.turnReachable,
        credentialsValid: norm.credentialsValid,
        expiresAt: norm.expiresAt,
      });
    }
    this.last = norm;

    // Restart only recovers a transient relay drop, and only when creds are still valid.
    if ((!norm.alive || !norm.turnReachable) && norm.credentialsValid) {
      const cooldown = this.opts.cooldownMs ?? 15000;
      if (now - this.lastEscalateTs >= cooldown) {
        this.lastEscalateTs = now;
        this.opts.onEscalate?.();
      }
    }

    // Proactive key rotation: re-mint BEFORE the credentials expire so they roll over with no
    // media interruption. Fire once per credential epoch (keyed by the expiry timestamp), and
    // re-arm automatically once a fresh expiry is observed after the swap.
    if (norm.credentialsValid && norm.expiresAt && this.opts.onRotateNeeded) {
      const expMs = Date.parse(norm.expiresAt);
      const lead = this.opts.rotateBeforeMs ?? 90_000;
      if (!Number.isNaN(expMs) && expMs - now <= lead && this.rotationSignalledFor !== norm.expiresAt) {
        this.rotationSignalledFor = norm.expiresAt;
        this.opts.onRotateNeeded(norm.expiresAt);
      }
    }
    return norm;
  }
}

export interface TransportDeps {
  platform: PlatformAdapter;
  signaling: SignalingClient;
  ice: IceConfig;
  livekitUrl?: string;
  whipEndpoint?: string;
  whepEndpoint?: string;
  /** Auth for the WHIP/WHEP media plane; keep distinct from signaling auth in production. */
  mediaToken?: string;
  token: string;
  selfId?: string;
  events?: EventBus;
  /** Keep-alive / state-machine sampling cadence in ms (clamped to 3–5 s, default 4 s). */
  keepAliveMs?: number;
  /**
   * The Signaling Infrastructure Provider chosen by the developer. When omitted,
   * `resolveProvider` infers it from the room (≤10 non-broadcast → matchmaker,
   * otherwise WHIP-WHEP), preserving the SDK's zero-config default.
   */
  provider?: SignalingProvider;
  fetch?: any;
  /** When set, every P2P media frame is end-to-end encrypted (relay sees ciphertext only). */
  e2ee?: E2eeContext;
  /** ICE transport policy passed to every PeerConnection ('all' default; 'relay' forces TURN). */
  iceTransportPolicy?: 'all' | 'relay';
}

/** Direct device↔device paths for N-way full-mesh calling up to 10 participants. */
export class P2PTransport implements Transport {
  readonly kind = 'p2p' as const;
  private pcs = new Map<string, RTCPeerConnection>();
  private makingOffers = new Map<string, boolean>();
  private ignoreOffers = new Map<string, boolean>();
  private politePeers = new Map<string, boolean>();
  private localStream?: MediaStream;
  private unsubscribeSignaling?: () => void;
  private participantsPollInterval?: any;
  private statsPollInterval?: any;
  private currentRoom?: Room;
  // Previous byte/timestamp sample per peer, used to derive live throughput (kbps)
  private statsHistory = new Map<string, StatsSample>();
  private qualitySmoothers = new Map<string, QualitySmoother>();
  private reconnectAttempts = new Map<string, number>();
  // Consecutive keep-alive ticks each peer has been not-alive (drives reconnect escalation)
  private staleTicks = new Map<string, number>();
  // ICE candidates that arrived before the remote description was set, buffered per peer
  private pendingCandidates = new Map<string, RTCIceCandidateInit[]>();
  // Control signaling messages buffered before the call is accepted, to prevent race conditions
  private pendingSignalingMessages = new Map<string, any[]>();
  // Active outgoing video override (e.g. screen-share composite). `undefined` = none.
  private videoOverride: MediaStreamTrack | null | undefined = undefined;
  // Per-peer received stream, so a UI can pause an off-screen tile's video (Meet grid).
  private remoteStreams = new Map<string, MediaStream>();

  // Kept for backward compatibility with 1:1 interfaces
  readonly remoteStream: MediaStream;
  // Local TURN-server liveness keep-alive (dedup + cooldown-gated reconnect).
  private liveness: TurnLivenessMonitor;

  constructor(private deps: TransportDeps) {
    this.remoteStream = this.makeStream();
    this.liveness = new TurnLivenessMonitor({
      events: this.deps.events,
      onEscalate: () => this.restartAllIce(),
      onRotateNeeded: (expiresAt) => this.emitRotationNeeded(expiresAt),
      fetch: this.deps.fetch,
    });
  }

  /** Surface proactive credential rotation to the orchestrator (zr-call re-mints + hot-swaps). */
  private emitRotationNeeded(expiresAt: string): void {
    const inMs = Date.parse(expiresAt) - Date.now();
    this.deps.events?.emit({ type: 'turn-credentials-expiring', expiresAt, inMs });
  }

  /** Restart ICE on every open connection (TURN-level recovery). */
  private restartAllIce(): void {
    console.warn('P2PTransport: TURN path down — restarting ICE on all peers.');
    for (const pc of this.pcs.values()) {
      if (pc.signalingState !== 'closed') {
        try {
          pc.restartIce();
        } catch {
          /* older RN-WebRTC may lack restartIce */
        }
      }
    }
  }

  /** Hot-swap re-minted TURN credentials onto every live peer connection. */
  async updateIceConfig(ice: IceConfig, restartIce = true): Promise<void> {
    this.deps.ice = ice;
    for (const pc of this.pcs.values()) {
      if (pc.signalingState === 'closed') continue;
      try {
        (pc as any).setConfiguration?.({ iceServers: ice.iceServers });
      } catch {
        /* setConfiguration unsupported on some RN-WebRTC builds */
      }
      // Proactive rotation swaps credentials with NO restart — the live path stays up and the
      // fresh creds are simply used for the next ICE allocation/refresh (seamless rollover).
      // Reactive recovery (dead creds) DOES restart, since the current path is already broken.
      if (restartIce) {
        try {
          pc.restartIce();
        } catch {
          /* ignore */
        }
      }
    }
  }

  /** Build a MediaStream via the platform adapter, falling back to the DOM global. */
  private makeStream(tracks?: MediaStreamTrack[]): MediaStream {
    if (this.deps.platform.createMediaStream) {
      return this.deps.platform.createMediaStream(tracks);
    }
    return tracks ? new MediaStream(tracks) : new MediaStream();
  }

  private async processSignalingMsg(msg: any): Promise<void> {
    if (!this.currentRoom) return;
    const room = this.currentRoom;
    const selfId = this.deps.selfId || 'local-peer';
    const remoteId = (msg as any).from;
    if (!remoteId || remoteId === selfId) return;

    if (msg.type === 'join') {
      if (room.mode === 'call') {
        if (!room.accepted) return;
        if (!(selfId < remoteId)) return;
      }
      if (!this.pcs.has(remoteId)) {
        this.getOrCreateConnection(remoteId, room);
      }
      return;
    }

    // A peer announced departure (graceful leave) — tear down its connection now
    // instead of waiting for the participant-poll TTL, so the grid updates instantly.
    if (msg.type === 'leave') {
      if (this.pcs.has(remoteId)) {
        this.closeConnection(remoteId);
        this.deps.events?.emit({ type: 'participant-left', participantId: remoteId });
      }
      return;
    }

    // Buffer media signaling until the call is accepted, preventing races where
    // offers arrive slightly before the accept/connecting state transitions.
    if (room.mode === 'call' && !room.accepted) {
      const q = this.pendingSignalingMessages.get(remoteId) || [];
      q.push(msg);
      this.pendingSignalingMessages.set(remoteId, q);
      return;
    }

    const pc = this.getOrCreateConnection(remoteId, room);
    try {
      if (msg.type === 'offer') {
        // Resolve polite role: lower string ID is polite
        const polite = selfId < remoteId;
        this.politePeers.set(remoteId, polite);

        const makingOffer = this.makingOffers.get(remoteId) || false;
        const offerCollision = makingOffer || pc.signalingState !== 'stable';
        const ignoreOffer = !polite && offerCollision;
        this.ignoreOffers.set(remoteId, ignoreOffer);

        if (ignoreOffer) {
          return;
        }

        if (offerCollision) {
          console.log(`P2PTransport: offer collision detected on polite peer ${remoteId}, rolling back local offer`);
          await pc.setLocalDescription({ type: 'rollback' });
        }

        // Plain RTCSessionDescriptionInit (no DOM RTCSessionDescription global) so the
        // engine stays portable to native runtimes; set*Description accepts the init.
        await pc.setRemoteDescription({ type: 'offer', sdp: msg.sdp });
        await this.flushCandidates(remoteId, pc);
        // Parameterless setLocalDescription lets the browser build the right SDP
        // (answer) for the current signaling state.
        await pc.setLocalDescription();

        this.deps.signaling.send({
          type: 'answer',
          roomId: room.id,
          to: remoteId,
          from: selfId,
          sdp: pc.localDescription!.sdp
        } as any);
      } else if (msg.type === 'answer') {
        await pc.setRemoteDescription({ type: 'answer', sdp: msg.sdp });
        await this.flushCandidates(remoteId, pc);
      } else if (msg.type === 'candidate') {
        const ignoreOffer = this.ignoreOffers.get(remoteId) || false;
        try {
          const candidateObj = JSON.parse(msg.candidate);
          if (!pc.remoteDescription) {
            // Buffer until the remote description exists, then flush in arrival order
            const q = this.pendingCandidates.get(remoteId) || [];
            q.push(candidateObj);
            this.pendingCandidates.set(remoteId, q);
          } else {
            const candidate = typeof RTCIceCandidate !== 'undefined' ? new RTCIceCandidate(candidateObj) : candidateObj;
            await pc.addIceCandidate(candidate);
          }
        } catch (err) {
          if (!ignoreOffer) {
            console.warn(`P2PTransport: Could not apply ICE candidate from ${remoteId}`, err);
          }
        }
      }
    } catch (err) {
      console.error(`P2PTransport: error handling signaling message from ${remoteId}:`, err);
    }
  }

  async flushPendingSignaling(remoteId: string): Promise<void> {
    const msgs = this.pendingSignalingMessages.get(remoteId);
    if (!msgs || msgs.length === 0) return;
    this.pendingSignalingMessages.delete(remoteId);
    for (const msg of msgs) {
      await this.processSignalingMsg(msg);
    }
  }

  async connect(room: Room): Promise<void> {
    this.currentRoom = room;
    const selfId = this.deps.selfId || 'local-peer';
    // 1. Subscribe to incoming signaling messages and route to appropriate peer connection
    this.unsubscribeSignaling = this.deps.signaling.on(async (msg) => {
      if (!('roomId' in msg) || msg.roomId !== room.id) return;
      await this.processSignalingMsg(msg);
    });

    await this.deps.signaling.joinRoom?.(room.id);

    const keepAliveMs = clampKeepAlive(this.deps.keepAliveMs);
    void this.keepAliveTick(room).catch((err) => {
      console.warn('P2PTransport: initial keep-alive tick failed:', err);
    });
    this.statsPollInterval = setInterval(async () => {
      try {
        await this.keepAliveTick(room);
      } catch (err) {
        console.warn('P2PTransport: keep-alive tick failed:', err);
      }
    }, keepAliveMs);

    // 2. Start polling for active participants list to establish mesh routes
    if (this.deps.signaling.getParticipants) {
      const pollParticipants = async () => {
        try {
          const activePeersList = await this.deps.signaling.getParticipants!(room.id);
          const activeRemotePeers = activePeersList.filter(id => id !== selfId);

          // Connect to newly joined peers
          for (const remoteId of activeRemotePeers) {
            // For 1:1 VoIP calls, only connect once the call has been accepted
            if (room.mode === 'call') {
              if (!room.accepted) {
                continue;
              }
              // To prevent offer collision/deadlock on local and public networks,
              // only the polite peer initiates the connection.
              const isPolite = selfId < remoteId;
              if (!isPolite) {
                continue;
              }
            }
            if (!this.pcs.has(remoteId)) {
              console.log(`P2PTransport: discovered new peer ${remoteId}, creating WebRTC connection`);
              this.getOrCreateConnection(remoteId, room);
            }
          }

          // Disconnect peers who have left
          for (const remoteId of this.pcs.keys()) {
            if (!activeRemotePeers.includes(remoteId)) {
              console.log(`P2PTransport: peer ${remoteId} disconnected, cleaning up resources`);
              this.closeConnection(remoteId);
              this.deps.events?.emit({
                type: 'participant-left',
                participantId: remoteId
              });
            }
          }
        } catch (err) {
          console.warn('P2PTransport: failed in participants discovery poll:', err);
        }
      };

      // Run initial check and set interval
      await pollParticipants();
      this.participantsPollInterval = setInterval(pollParticipants, 3000);
    }
  }

  /** Applies any ICE candidates buffered before the remote description was set. */
  private async flushCandidates(remoteId: string, pc: RTCPeerConnection): Promise<void> {
    const q = this.pendingCandidates.get(remoteId);
    if (!q || q.length === 0) return;
    this.pendingCandidates.delete(remoteId);
    for (const c of q) {
      try {
        const candidate = typeof RTCIceCandidate !== 'undefined' ? new RTCIceCandidate(c) : c;
        await pc.addIceCandidate(candidate);
      } catch {
        // ignore late/invalid candidates
      }
    }
  }

  /** Adds a track only if an identical one isn't already on a sender (prevents duplicate m-lines). */
  private addTrackOnce(pc: RTCPeerConnection, track: MediaStreamTrack, stream: MediaStream): void {
    const exists = pc.getSenders().some((s) => s.track === track);
    if (!exists) {
      const sender = pc.addTrack(track, stream);
      // Seal outgoing frames the moment the sender exists (encoded streams must be
      // claimed before the first frame flows).
      this.deps.e2ee?.attachSender(sender);
    }
  }

  /**
   * Keep-alive tick (3–5 s). Passively samples each PeerConnection — it reads the counters
   * the live RTP flow and the STUN/TURN-kept ICE path already maintain (getStats), so it
   * never injects probe traffic. For every peer it:
   *   • derives transport quality (RTT/loss/jitter/throughput/BWE) → `quality-changed`,
   *   • reads the active candidate pair to expose whether the path is a TURN relay and
   *     whether it is TCP/TLS vs UDP (transport visibility),
   *   • maintains the real-time connection state machine + liveness, emitting a single
   *     consolidated `peer-heartbeat` per peer, and
   *   • escalates an ICE restart when a peer has gone stale across consecutive ticks.
   */
  private async keepAliveTick(room: Room) {
    // Probe the authenticated CoTURN service for this peer's TURN liveness (dedup +
    // cooldown-gated ICE restart live inside the monitor).
    const ice = this.deps.ice;
    if (ice.turnApiBase && ice.turnToken && ice.turnUsername) {
      await this.liveness.tick(
        { turnApiBase: ice.turnApiBase, turnToken: ice.turnToken, turnUsername: ice.turnUsername },
        Date.now(),
      );
    }

    for (const [remoteId, pc] of this.pcs.entries()) {
      if (pc.signalingState === 'closed') continue;

      try {
        const stats = await pc.getStats();
        // Sample raw counters, then fold against the previous sample for WINDOWED loss +
        // throughput (accurate "right now" telemetry rather than a session-cumulative average).
        const now = Date.now();
        const sample = extractStatsSample(stats, now);
        const prev = this.statsHistory.get(remoteId);
        const { details, bytesDelta } = computeQualityDetails(sample, prev);
        this.statsHistory.set(remoteId, sample);

        const iceState = pc.iceConnectionState as IceConnectionState;
        const transport = extractPeerTransport(stats);

        // Classify with loss/jitter primary + RTT relaxed for relayed (TURN) paths, then debounce
        // the LEVEL per peer so it doesn't flap on single-tick RTT spikes.
        let { score } = classifyQuality({ ...details, jitter: details.jitter, relayed: !!transport?.usingTurn });
        if (iceState !== 'connected' && iceState !== 'completed') score = 1;
        let smoother = this.qualitySmoothers.get(remoteId);
        if (!smoother) { smoother = new QualitySmoother(); this.qualitySmoothers.set(remoteId, smoother); }
        score = smoother.update(score);
        const level = qualityLevelForScore(score);

        this.deps.events?.emit({
          type: 'quality-changed',
          participantId: remoteId,
          score,
          level,
          details,
        });

        // --- Real-time connection state machine + transport intelligence ---
        const alive = isAlive({ bytesDelta, iceConnectionState: iceState });
        const stale = alive ? 0 : (this.staleTicks.get(remoteId) || 0) + 1;
        this.staleTicks.set(remoteId, stale);
        // A recovered peer clears its escalation counter.
        if (iceState === 'connected' || iceState === 'completed') this.reconnectAttempts.delete(remoteId);

        const heartbeat: PeerHeartbeat = {
          participantId: remoteId,
          iceConnectionState: iceState,
          connectionState: pc.connectionState ?? 'unknown',
          transport,
          quality: { score, level, details },
          alive,
          staleTicks: stale,
          ts: now,
        };
        this.deps.events?.emit({ type: 'peer-heartbeat', participantId: remoteId, heartbeat });

        // Escalate: a peer stale for ≥2 consecutive ticks (~6–10 s) gets an ICE restart so
        // we recover even if the connection lingers in 'disconnected' without a 'failed'.
        // We only escalate if the state is explicitly 'disconnected' or 'failed', OR if
        // the connection has been stuck in 'new'/'checking' for a long duration (e.g. 10 ticks ~40s).
        const shouldRestart =
          ((iceState === 'disconnected' || iceState === 'failed') && stale >= 2) ||
          ((iceState === 'new' || iceState === 'checking') && stale >= 10);

        if (shouldRestart) {
          const attempts = (this.reconnectAttempts.get(remoteId) || 0) + 1;
          this.reconnectAttempts.set(remoteId, attempts);
          this.staleTicks.set(remoteId, 0); // pace both peers to ~one attempt per 2 ticks
          // Only the impolite peer restarts ICE, so both sides don't restart at once.
          if (!this.politePeers.get(remoteId)) {
            try {
              console.warn(`P2PTransport: reconnect attempt ${attempts} for ${remoteId} (state: ${iceState}) — restarting ICE.`);
              pc.restartIce();
            } catch { /* older RN-WebRTC may lack restartIce */ }
          }
          // ESCALATE every 3rd failed attempt: an ICE restart can't recover a DEAD/expired TURN
          // allocation (the storm the QA logs showed). Ask the orchestrator to re-mint TURN
          // credentials + hot-swap a fresh relay. Both peers escalate (re-mint is local).
          if (attempts % 3 === 0) {
            console.warn(`P2PTransport: ${remoteId} still down after ${attempts} restarts — escalating to TURN credential re-mint.`);
            this.deps.events?.emit({ type: 'peer-reconnect-escalate', participantId: remoteId, attempts });
          }
          // Give up after a prolonged outage so the session doesn't loop "restarting ICE" forever.
          if (attempts >= 12) {
            console.warn(`P2PTransport: ${remoteId} unrecoverable after ${attempts} attempts — marking connection failed.`);
            this.deps.events?.emit({ type: 'peer-connection-state-changed', participantId: remoteId, state: 'failed' });
            this.reconnectAttempts.set(remoteId, 0);
          }
        }
      } catch (e) {
        console.warn(`P2PTransport: keep-alive getStats failed for peer ${remoteId}:`, e);
      }
    }
  }

  private getOrCreateConnection(remoteId: string, room: Room): RTCPeerConnection {
    const existing = this.pcs.get(remoteId);
    if (existing) return existing;

    const selfId = this.deps.selfId || 'local-peer';
    // Enable Insertable Streams ONLY when E2EE is active (a transform WILL be attached).
    // Enabling it otherwise makes Chrome hold all frames → 0 bytes sent → no media.
    const pc = this.deps.platform.createPeerConnection(this.deps.ice, remoteId, selfId, {
      encodedInsertableStreams: !!this.deps.e2ee,
      iceTransportPolicy: this.deps.iceTransportPolicy,
    });
    this.pcs.set(remoteId, pc);

    // Track state
    this.makingOffers.set(remoteId, false);
    this.ignoreOffers.set(remoteId, false);
    this.politePeers.set(remoteId, selfId < remoteId);

    // Setup negotiation offer trigger
    pc.onnegotiationneeded = async () => {
      if (pc.signalingState !== 'stable') {
        return;
      }
      try {
        this.makingOffers.set(remoteId, true);
        // Parameterless setLocalDescription builds the correct offer for the state.
        await pc.setLocalDescription();
        this.deps.signaling.send({
          type: 'offer',
          roomId: room.id,
          to: remoteId,
          from: selfId,
          sdp: pc.localDescription!.sdp
        } as any);
      } catch (err) {
        console.error(`P2PTransport: error in onnegotiationneeded for peer ${remoteId}:`, err);
      } finally {
        this.makingOffers.set(remoteId, false);
      }
    };

    // Reconnection: drive ICE restart from connection-state changes. Critical on the
    // lossy African mobile networks this SDK targets, where transient drops are routine.
    pc.onconnectionstatechange = () => {
      const st = pc.connectionState;
      if (st === 'failed') {
        // Only the impolite peer restarts ICE, so both sides don't restart at once.
        if (!this.politePeers.get(remoteId)) {
          try { pc.restartIce(); } catch { /* older RN-WebRTC may lack restartIce */ }
        }
        this.deps.events?.emit({ type: 'state-changed', state: 'reconnecting' });
      } else if (st === 'disconnected') {
        this.deps.events?.emit({ type: 'state-changed', state: 'reconnecting' });
      } else if (st === 'connected') {
        this.deps.events?.emit({ type: 'state-changed', state: 'connected' });
      }
    };

    pc.oniceconnectionstatechange = () => {
      this.deps.events?.emit({
        type: 'peer-connection-state-changed',
        participantId: remoteId,
        state: pc.iceConnectionState as IceConnectionState,
      });
    };

    // Setup candidate dispatch
    pc.onicecandidate = ({ candidate }) => {
      if (candidate) {
        this.deps.signaling.send({
          type: 'candidate',
          roomId: room.id,
          to: remoteId,
          from: selfId,
          candidate: JSON.stringify({
            candidate: candidate.candidate,
            sdpMid: candidate.sdpMid,
            sdpMLineIndex: candidate.sdpMLineIndex,
            usernameFragment: candidate.usernameFragment
          })
        } as any);
      }
    };

    // Setup incoming track hooks
    pc.ontrack = (event) => {
      // Decrypt this remote peer's frames (their identity key) before they reach the decoder.
      if (event.receiver) this.deps.e2ee?.attachReceiver(event.receiver, remoteId);
      const stream = event.streams[0] || this.makeStream([event.track]);
      // Track the per-peer stream so a UI can pause an off-screen tile (Meet grid).
      this.remoteStreams.set(remoteId, stream);

      // Update legacy remoteStream for backward-compatibility checks
      if (event.track) {
        this.remoteStream.addTrack(event.track);
      }

      this.deps.events?.emit({
        type: 'participant-joined',
        participant: {
          id: remoteId,
          displayName: remoteId,
          isLocal: false,
          isHost: false,
          tracks: [{ id: event.track.id, kind: event.track.kind as any, muted: !event.track.enabled }]
        }
      });

      this.deps.events?.emit({
        type: 'track-subscribed',
        participantId: remoteId,
        track: {
          id: event.track.id,
          kind: event.track.kind as any,
          muted: !event.track.enabled,
          stream // Attach actual MediaStream dynamically so the UI can draw it
        } as any
      });
    };

    // Add local stream tracks immediately if already active
    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => {
        this.addTrackOnce(pc, track, this.localStream!);
      });
    }

    // If a video override (e.g. an active screen-share composite) is set, a peer that
    // joins mid-share must publish the composite too, not the raw camera track.
    if (this.videoOverride !== undefined) {
      const sender = pc.getSenders().find((s) => s.track?.kind === 'video');
      if (sender) sender.replaceTrack(this.videoOverride).catch(() => {});
    }

    // Force initial negotiation if we are the polite peer (initiator) to handle
    // cases where the browser does not fire onnegotiationneeded for initial tracks.
    const isPolite = selfId < remoteId;
    if (isPolite && this.localStream && this.localStream.getTracks().length > 0) {
      setTimeout(async () => {
        if (pc.signalingState === 'stable' && !this.makingOffers.get(remoteId)) {
          try {
            console.log(`P2PTransport: forcing initial negotiation offer to ${remoteId}`);
            this.makingOffers.set(remoteId, true);
            await pc.setLocalDescription();
            this.deps.signaling.send({
              type: 'offer',
              roomId: room.id,
              to: remoteId,
              from: selfId,
              sdp: pc.localDescription!.sdp
            } as any);
          } catch (err) {
            console.error(`P2PTransport: error in forced initial negotiation for peer ${remoteId}:`, err);
          } finally {
            this.makingOffers.set(remoteId, false);
          }
        }
      }, 50);
    }

    return pc;
  }

  private closeConnection(remoteId: string) {
    const pc = this.pcs.get(remoteId);
    if (pc) {
      pc.close();
      this.pcs.delete(remoteId);
    }
    this.makingOffers.delete(remoteId);
    this.ignoreOffers.delete(remoteId);
    this.politePeers.delete(remoteId);
    this.statsHistory.delete(remoteId);
    this.qualitySmoothers.delete(remoteId);
    this.reconnectAttempts.delete(remoteId);
    this.staleTicks.delete(remoteId);
    this.pendingCandidates.delete(remoteId);
    this.remoteStreams.delete(remoteId);
  }

  /** Meet off-screen optimization: pause/resume a remote peer's received video track. */
  setRemoteVideoEnabled(participantId: string, enabled: boolean): void {
    this.remoteStreams.get(participantId)?.getVideoTracks().forEach((t) => (t.enabled = enabled));
  }

  /** The received stream for a specific participant (per-participant model). */
  getParticipantStream(participantId: string): MediaStream | undefined {
    return this.remoteStreams.get(participantId);
  }

  /** All received per-participant streams keyed by participant id (read-only snapshot). */
  get participantStreams(): Map<string, MediaStream> {
    return new Map(this.remoteStreams);
  }

  async publish(stream: MediaStream): Promise<void> {
    this.localStream = stream;
    const selfId = this.deps.selfId || 'local-peer';
    // Add local tracks to all current and future connections (dedup-safe)
    this.pcs.forEach((pc, remoteId) => {
      stream.getTracks().forEach((track) => {
        this.addTrackOnce(pc, track, stream);
      });

      // Force initial negotiation fallback if polite peer (initiator)
      const isPolite = selfId < remoteId;
      if (isPolite && stream.getTracks().length > 0) {
        setTimeout(async () => {
          if (pc.signalingState === 'stable' && !this.makingOffers.get(remoteId)) {
            try {
              console.log(`P2PTransport: forcing initial negotiation offer from publish to ${remoteId}`);
              this.makingOffers.set(remoteId, true);
              await pc.setLocalDescription();
              this.deps.signaling.send({
                type: 'offer',
                roomId: this.currentRoom?.id || '',
                to: remoteId,
                from: selfId,
                sdp: pc.localDescription!.sdp
              } as any);
            } catch (err) {
              console.error(`P2PTransport: error in forced initial negotiation from publish for peer ${remoteId}:`, err);
            } finally {
              this.makingOffers.set(remoteId, false);
            }
          }
        }, 50);
      }
    });
  }

  /** Swaps the outgoing video track on every peer (used for screen share / camera switch). */
  async replaceVideoTrack(track: MediaStreamTrack | null): Promise<void> {
    // Remember it so peers that join later also get this track, not the raw camera.
    this.videoOverride = track;
    for (const pc of this.pcs.values()) {
      const sender = pc.getSenders().find((s) => s.track?.kind === 'video');
      if (sender) await sender.replaceTrack(track);
    }
  }

  async close(announceLeave = true): Promise<void> {
    // Gracefully announce departure to every peer so their grid updates immediately,
    // instead of each peer waiting for the participant-poll TTL to age us out. Sent while
    // signaling is still open (zr-call closes signaling only after this returns).
    if (announceLeave && this.currentRoom) {
      const selfId = this.deps.selfId || 'local-peer';
      for (const remoteId of this.pcs.keys()) {
        try {
          this.deps.signaling.send({ type: 'leave', roomId: this.currentRoom.id, to: remoteId, from: selfId });
        } catch {
          /* best effort */
        }
      }
    }
    if (this.currentRoom) {
      try {
        await this.deps.signaling.leaveRoom?.(this.currentRoom.id);
      } catch {
        /* best effort */
      }
    }

    if (this.participantsPollInterval) {
      clearInterval(this.participantsPollInterval);
      this.participantsPollInterval = undefined;
    }
    if (this.statsPollInterval) {
      clearInterval(this.statsPollInterval);
      this.statsPollInterval = undefined;
    }
    if (this.unsubscribeSignaling) {
      this.unsubscribeSignaling();
      this.unsubscribeSignaling = undefined;
    }
    
    // Close all connections
    this.pcs.forEach((pc, remoteId) => {
      pc.close();
    });
    this.pcs.clear();
    this.makingOffers.clear();
    this.ignoreOffers.clear();
    this.politePeers.clear();
    this.statsHistory.clear();
    this.staleTicks.clear();
    this.pendingCandidates.clear();
    this.remoteStreams.clear();

    this.remoteStream.getTracks().forEach((t) => {
      t.stop();
      this.remoteStream.removeTrack(t);
    });

    // Release the local camera/mic so the device indicator turns off after the call.
    if (this.localStream) {
      this.localStream.getTracks().forEach((t) => t.stop());
      this.localStream = undefined;
    }
  }
}

/**
 * SFU path — conference rooms connect through the self-hosted LiveKit room WebSocket with
 * manager-minted room tokens; broadcast sessions keep the WHIP/WHEP ingress/egress path.
 *
 * This gives `provider: 'whip-whep'` true room-native many-to-many behaviour for call/group
 * sessions while preserving the existing single-program broadcast model for egress viewers.
 */
export class SfuTransport implements Transport {
  readonly kind = 'sfu' as const;
  readonly remoteStream: MediaStream;

  private publishPc?: RTCPeerConnection;
  private subscribePc?: RTCPeerConnection;
  private whip?: WhipClient;
  private whep?: WhepClient;
  private livekitRoom?: LiveKitRoom;
  private currentRoom?: Room;
  private localStream?: MediaStream;
  private statsPollInterval?: any;
  private liveness: TurnLivenessMonitor;
  private programStats?: StatsSample;
  private programSmoother = new QualitySmoother();
  private roomStats = new Map<string, StatsSample>();
  private roomStaleTicks = new Map<string, number>();
  private roomSmoothers = new Map<string, QualitySmoother>();
  private remoteStreams = new Map<string, MediaStream>();
  private remoteParticipants = new Map<string, LiveKitRemoteParticipant>();
  private announcedParticipants = new Set<string>();
  private participantIceStates = new Map<string, IceConnectionState>();
  private localPublishedTracks = new Map<'audio' | 'video', MediaStreamTrack>();

  private static readonly PROGRAM_ID = 'program';

  constructor(private deps: TransportDeps) {
    this.remoteStream = this.makeStream();
    this.liveness = new TurnLivenessMonitor({
      events: this.deps.events,
      onEscalate: () => this.restartAllIce(),
      onRotateNeeded: (expiresAt) => {
        const inMs = Date.parse(expiresAt) - Date.now();
        this.deps.events?.emit({ type: 'turn-credentials-expiring', expiresAt, inMs });
      },
      fetch: this.deps.fetch,
    });
  }

  private get authToken(): string | undefined {
    return this.deps.mediaToken || this.deps.token;
  }

  private get isBroadcastMode(): boolean {
    return (
      this.currentRoom?.mode === 'broadcast' ||
      (!this.currentRoom && !!this.deps.whipEndpoint && !this.deps.livekitUrl) ||
      !!this.publishPc ||
      !!this.subscribePc ||
      !!this.whip ||
      !!this.whep
    );
  }

  /** Restart ICE on the broadcast ingress + egress connections (TURN-level recovery). */
  private restartAllIce(): void {
    if (!this.isBroadcastMode) return;
    console.warn('SfuTransport: TURN path down — restarting broadcast ingress/egress.');
    for (const pc of [this.publishPc, this.subscribePc]) {
      if (pc && pc.signalingState !== 'closed') {
        try {
          pc.restartIce();
        } catch {
          /* ignore */
        }
      }
    }
  }

  /** Hot-swap re-minted TURN credentials onto the broadcast ingress/egress connections. */
  async updateIceConfig(ice: IceConfig, restartIce = true): Promise<void> {
    this.deps.ice = ice;
    if (!this.isBroadcastMode) return;
    for (const pc of [this.publishPc, this.subscribePc]) {
      if (!pc || pc.signalingState === 'closed') continue;
      try {
        (pc as any).setConfiguration?.({ iceServers: ice.iceServers });
      } catch {
        /* unsupported on some builds */
      }
      if (restartIce) {
        try {
          pc.restartIce();
        } catch {
          /* ignore */
        }
      }
    }
  }

  private makeStream(tracks?: MediaStreamTrack[]): MediaStream {
    if (this.deps.platform.createMediaStream) return this.deps.platform.createMediaStream(tracks);
    return tracks ? new MediaStream(tracks) : new MediaStream();
  }

  private zeroDetails(): QualityDetails {
    return {
      rtt: 0,
      lossRate: 0,
      jitter: 0,
      downKbps: 0,
      upKbps: 0,
      availableOutgoingKbps: 0,
    };
  }

  private mapTrackKind(kind: string | undefined, source?: string): 'audio' | 'video' | 'screen' {
    if (source === LiveKitTrack.Source.ScreenShare || source === LiveKitTrack.Source.ScreenShareAudio) {
      return 'screen';
    }
    if (kind === LiveKitTrack.Kind.Audio || kind === 'audio') return 'audio';
    return 'video';
  }

  private livekitParticipantId(participant: Pick<LiveKitParticipant, 'identity' | 'sid'>): string {
    return participant.identity || participant.sid || 'unknown-participant';
  }

  private livekitParticipantName(participant: Pick<LiveKitParticipant, 'identity' | 'sid' | 'name'>): string {
    return participant.name || participant.identity || participant.sid || 'Participant';
  }

  private currentRoomIceState(): IceConnectionState {
    const state = String(this.livekitRoom?.state ?? 'disconnected');
    switch (state) {
      case 'connected':
        return 'connected';
      case 'connecting':
        return 'checking';
      case 'reconnecting':
      case 'signalReconnecting':
        return 'disconnected';
      case 'disconnected':
      default:
        return 'closed';
    }
  }

  private emitParticipantIceState(participantId: string, state: IceConnectionState): void {
    const prev = this.participantIceStates.get(participantId);
    if (prev === state) return;
    if (state === 'closed') this.participantIceStates.delete(participantId);
    else this.participantIceStates.set(participantId, state);
    this.deps.events?.emit({
      type: 'peer-connection-state-changed',
      participantId,
      state,
    });
  }

  private participantTracks(participant: LiveKitRemoteParticipant) {
    return Array.from(participant.trackPublications.values()).map((publication) => ({
      id: publication.trackSid || publication.track?.sid || publication.trackName || `${this.livekitParticipantId(participant)}-${publication.kind}`,
      kind: this.mapTrackKind(publication.kind, publication.source),
      muted: publication.isMuted || !publication.isEnabled,
    }));
  }

  private ensureParticipantPresent(participant: LiveKitRemoteParticipant): void {
    const participantId = this.livekitParticipantId(participant);
    this.remoteParticipants.set(participantId, participant);
    if (this.announcedParticipants.has(participantId)) return;
    this.announcedParticipants.add(participantId);
    this.deps.events?.emit({
      type: 'participant-joined',
      participant: {
        id: participantId,
        displayName: this.livekitParticipantName(participant),
        isLocal: false,
        isHost: false,
        tracks: this.participantTracks(participant),
      },
    });
    this.emitParticipantIceState(participantId, this.currentRoomIceState() === 'closed' ? 'checking' : this.currentRoomIceState());
  }

  private removeTrackFromStream(stream: MediaStream | undefined, track: MediaStreamTrack): void {
    if (!stream) return;
    if (typeof stream.getTracks === 'function' && stream.getTracks().includes(track)) {
      stream.removeTrack(track);
    }
  }

  private mergeStats(base: StatsSample | undefined, next: StatsSample | undefined, now: number): StatsSample | undefined {
    if (!next) return base;
    if (!base) return { ...next, ts: now };
    return {
      ts: now,
      bytesRecv: base.bytesRecv + next.bytesRecv,
      bytesSent: base.bytesSent + next.bytesSent,
      packetsLost: base.packetsLost + next.packetsLost,
      packetsReceived: base.packetsReceived + next.packetsReceived,
      rtt: Math.max(base.rtt, next.rtt),
      jitter: Math.max(base.jitter, next.jitter),
      availableOutgoingKbps: Math.max(base.availableOutgoingKbps, next.availableOutgoingKbps),
    };
  }

  private async collectLiveKitSample(
    publications: Iterable<{ track?: { getRTCStatsReport?: () => Promise<RTCStatsReport | undefined> } }>,
    now: number,
  ): Promise<{ sample?: StatsSample; transport?: ReturnType<typeof extractPeerTransport> }> {
    let merged: StatsSample | undefined;
    let transport: ReturnType<typeof extractPeerTransport>;
    for (const publication of publications) {
      const stats = await publication.track?.getRTCStatsReport?.();
      if (!stats) continue;
      const sample = extractStatsSample(stats, now);
      merged = this.mergeStats(merged, sample, now);
      if (!transport) transport = extractPeerTransport(stats);
    }
    return { sample: merged, transport };
  }

  private mapLiveKitQuality(quality: LiveKitConnectionQuality): { score: number; level: 'excellent' | 'good' | 'fair' | 'poor' } {
    switch (quality) {
      case LiveKitConnectionQuality.Excellent:
        return { score: 4, level: 'excellent' };
      case LiveKitConnectionQuality.Good:
        return { score: 3, level: 'good' };
      case LiveKitConnectionQuality.Poor:
        return { score: 2, level: 'fair' };
      case LiveKitConnectionQuality.Lost:
      case LiveKitConnectionQuality.Unknown:
      default:
        return { score: 1, level: 'poor' };
    }
  }

  /** Forward a PeerConnection's ICE state to the call-phase machine. */
  private wireIceState(pc: RTCPeerConnection, id: string): void {
    pc.oniceconnectionstatechange = () => {
      this.deps.events?.emit({
        type: 'peer-connection-state-changed',
        participantId: id,
        state: pc.iceConnectionState as IceConnectionState,
      });
    };
  }

  private handleLiveKitActiveSpeakers(speakers: LiveKitParticipant[]): void {
    this.deps.events?.emit({
      type: 'active-speakers-changed',
      speakers: speakers.map((speaker) => ({
        participantId: this.livekitParticipantId(speaker),
        level: Math.round((speaker.audioLevel || 0) * 1000) / 1000,
        isLocal: !!speaker.isLocal,
      })),
    });
  }

  private handleLiveKitParticipantDisconnected(participant: LiveKitRemoteParticipant): void {
    const participantId = this.livekitParticipantId(participant);
    const stream = this.remoteStreams.get(participantId);
    stream?.getTracks().forEach((track) => this.removeTrackFromStream(this.remoteStream, track));
    this.remoteStreams.delete(participantId);
    this.remoteParticipants.delete(participantId);
    this.announcedParticipants.delete(participantId);
    this.roomStats.delete(participantId);
    this.roomStaleTicks.delete(participantId);
    this.emitParticipantIceState(participantId, 'closed');
    this.deps.events?.emit({ type: 'participant-left', participantId });
  }

  private handleLiveKitTrackSubscribed(
    track: LiveKitRemoteTrack,
    publication: LiveKitRemoteTrackPublication,
    participant: LiveKitRemoteParticipant,
  ): void {
    const participantId = this.livekitParticipantId(participant);
    this.ensureParticipantPresent(participant);
    let stream = this.remoteStreams.get(participantId);
    if (!stream) {
      stream = this.makeStream();
      this.remoteStreams.set(participantId, stream);
    }
    const mediaTrack = track.mediaStreamTrack;
    if (mediaTrack && !stream.getTracks().includes(mediaTrack)) {
      stream.addTrack(mediaTrack);
    }
    if (mediaTrack && !this.remoteStream.getTracks().includes(mediaTrack)) {
      this.remoteStream.addTrack(mediaTrack);
    }
    this.emitParticipantIceState(participantId, 'connected');
    this.deps.events?.emit({
      type: 'track-subscribed',
      participantId,
      track: {
        id: mediaTrack?.id || publication.trackSid || publication.trackName || `${participantId}-${publication.kind}`,
        kind: this.mapTrackKind(track.kind, publication.source),
        muted: publication.isMuted || !mediaTrack?.enabled,
        stream,
      } as any,
    });
  }

  private handleLiveKitTrackUnsubscribed(
    track: LiveKitRemoteTrack,
    _publication: LiveKitRemoteTrackPublication,
    participant: LiveKitRemoteParticipant,
  ): void {
    const participantId = this.livekitParticipantId(participant);
    const mediaTrack = track.mediaStreamTrack;
    if (!mediaTrack) return;
    this.removeTrackFromStream(this.remoteStreams.get(participantId), mediaTrack);
    this.removeTrackFromStream(this.remoteStream, mediaTrack);
  }

  private handleLiveKitConnectionQualityChanged(
    quality: LiveKitConnectionQuality,
    participant: LiveKitParticipant,
  ): void {
    if (participant.isLocal) return;
    const participantId = this.livekitParticipantId(participant);
    const { score, level } = this.mapLiveKitQuality(quality);
    this.deps.events?.emit({
      type: 'quality-changed',
      participantId,
      score,
      level,
      details: this.zeroDetails(),
    });
    this.emitParticipantIceState(participantId, quality === LiveKitConnectionQuality.Lost ? 'disconnected' : 'connected');
  }

  private wireLiveKitRoomEvents(room: LiveKitRoom): void {
    room.on(LiveKitRoomEvent.ParticipantConnected, (participant) => {
      this.ensureParticipantPresent(participant);
    });
    room.on(LiveKitRoomEvent.ParticipantDisconnected, (participant) => {
      this.handleLiveKitParticipantDisconnected(participant);
    });
    room.on(LiveKitRoomEvent.TrackSubscribed, (track, publication, participant) => {
      this.handleLiveKitTrackSubscribed(track, publication, participant);
    });
    room.on(LiveKitRoomEvent.TrackUnsubscribed, (track, publication, participant) => {
      this.handleLiveKitTrackUnsubscribed(track, publication, participant);
    });
    room.on(LiveKitRoomEvent.TrackUnpublished, (publication, participant) => {
      const mediaTrack = publication.track?.mediaStreamTrack;
      if (mediaTrack) this.handleLiveKitTrackUnsubscribed(publication.track as LiveKitRemoteTrack, publication, participant);
    });
    room.on(LiveKitRoomEvent.ActiveSpeakersChanged, (speakers) => {
      this.handleLiveKitActiveSpeakers(speakers);
    });
    room.on(LiveKitRoomEvent.ConnectionQualityChanged, (quality, participant) => {
      this.handleLiveKitConnectionQualityChanged(quality, participant);
    });
    room.on(LiveKitRoomEvent.Reconnecting, () => {
      this.deps.events?.emit({ type: 'state-changed', state: 'reconnecting' });
      for (const participantId of this.remoteParticipants.keys()) {
        this.emitParticipantIceState(participantId, 'disconnected');
      }
    });
    room.on(LiveKitRoomEvent.Reconnected, () => {
      this.deps.events?.emit({ type: 'state-changed', state: 'connected' });
      for (const participantId of this.remoteParticipants.keys()) {
        this.emitParticipantIceState(participantId, 'connected');
      }
    });
    room.on(LiveKitRoomEvent.Disconnected, () => {
      for (const participant of Array.from(this.remoteParticipants.values())) {
        this.handleLiveKitParticipantDisconnected(participant);
      }
    });
  }

  private async connectBroadcast(): Promise<void> {
    if (!this.deps.whepEndpoint) return;

    const pc = this.deps.platform.createPeerConnection(this.deps.ice);
    this.subscribePc = pc;
    pc.addTransceiver('audio', { direction: 'recvonly' });
    pc.addTransceiver('video', { direction: 'recvonly' });
    this.wireIceState(pc, SfuTransport.PROGRAM_ID);

    pc.ontrack = (event) => {
      const stream = event.streams[0] || this.makeStream([event.track]);
      if (event.track) this.remoteStream.addTrack(event.track);
      this.deps.events?.emit({
        type: 'participant-joined',
        participant: {
          id: SfuTransport.PROGRAM_ID,
          displayName: 'Program',
          isLocal: false,
          isHost: false,
          tracks: [{ id: event.track.id, kind: event.track.kind as any, muted: !event.track.enabled }],
        },
      });
      this.deps.events?.emit({
        type: 'track-subscribed',
        participantId: SfuTransport.PROGRAM_ID,
        track: { id: event.track.id, kind: event.track.kind as any, muted: !event.track.enabled, stream } as any,
      });
    };

    this.whep = new WhepClient(this.deps.whepEndpoint, this.authToken);
    await this.whep.play(pc);
  }

  private async connectLiveKitRoom(): Promise<void> {
    if (!this.deps.livekitUrl) {
      throw new Error(
        `${PROVIDER_LABELS['whip-whep'].name} conference rooms require a livekitUrl (room WebSocket URL). ` +
          `Return it from your manager bootstrap endpoint or pass it directly on the client config.`,
      );
    }
    if (!this.authToken) {
      throw new Error(`${PROVIDER_LABELS['whip-whep'].name} conference rooms require a media token.`);
    }

    const room = new LiveKitRoom({ adaptiveStream: true, dynacast: true });
    this.livekitRoom = room;
    this.wireLiveKitRoomEvents(room);
    await room.connect(this.deps.livekitUrl, this.authToken, { autoSubscribe: true });

    for (const participant of room.remoteParticipants.values()) {
      this.ensureParticipantPresent(participant);
    }
    this.handleLiveKitActiveSpeakers(room.activeSpeakers || []);
    this.deps.events?.emit({ type: 'state-changed', state: 'connected' });
  }

  async connect(room: Room): Promise<void> {
    this.currentRoom = room;
    if (room.mode === 'broadcast') {
      await this.connectBroadcast();
    } else {
      await this.connectLiveKitRoom();
    }
    this.startKeepAlive();
  }

  private async publishBroadcast(stream: MediaStream): Promise<void> {
    if (!this.deps.whipEndpoint) {
      throw new Error(
        `${PROVIDER_LABELS['whip-whep'].name} broadcasts require a whipEndpoint to publish (stream ingress). ` +
          `Configure it on the client, or use provider: 'serverless-matchmaker' for ≤${MAX_MATCHMAKER_PARTICIPANTS} P2P participants.`,
      );
    }
    this.localStream = stream;
    const pc = this.deps.platform.createPeerConnection(this.deps.ice);
    this.publishPc = pc;
    this.wireIceState(pc, 'self-ingress');
    stream.getTracks().forEach((track) => pc.addTrack(track, stream));

    this.whip = new WhipClient(this.deps.whipEndpoint, this.authToken);
    await this.whip.publish(pc);
  }

  private async publishLiveKitRoom(stream: MediaStream): Promise<void> {
    if (!this.livekitRoom) {
      throw new Error('LiveKit room is not connected yet; call connect() before publish().');
    }
    this.localStream = stream;
    for (const track of stream.getTracks()) {
      const kind = track.kind === 'video' ? 'video' : 'audio';
      const prev = this.localPublishedTracks.get(kind);
      if (prev === track) continue;
      if (prev) {
        await this.livekitRoom.localParticipant.unpublishTrack(prev, false).catch(() => undefined);
      }
      await this.livekitRoom.localParticipant.publishTrack(track);
      this.localPublishedTracks.set(kind, track);
    }
  }

  async publish(stream: MediaStream): Promise<void> {
    if (this.isBroadcastMode) {
      await this.publishBroadcast(stream);
    } else {
      await this.publishLiveKitRoom(stream);
    }
    this.startKeepAlive();
  }

  /** Swap the outgoing video track (screen share / camera switch). */
  async replaceVideoTrack(track: MediaStreamTrack | null): Promise<void> {
    if (this.isBroadcastMode) {
      const sender = this.publishPc?.getSenders().find((s) => s.track?.kind === 'video');
      if (sender) await sender.replaceTrack(track);
      return;
    }
    if (!this.livekitRoom) return;
    const prev = this.localPublishedTracks.get('video');
    if (prev) {
      await this.livekitRoom.localParticipant.unpublishTrack(prev, false).catch(() => undefined);
      this.localPublishedTracks.delete('video');
    }
    if (track) {
      await this.livekitRoom.localParticipant.publishTrack(track);
      this.localPublishedTracks.set('video', track);
    }
  }

  /** Meet off-screen optimization on room tracks / broadcast program egress. */
  setRemoteVideoEnabled(participantId: string, enabled: boolean): void {
    if (this.isBroadcastMode) {
      this.remoteStream.getVideoTracks().forEach((t) => (t.enabled = enabled));
      return;
    }
    this.remoteParticipants.get(participantId)?.videoTrackPublications.forEach((publication) => {
      publication.setEnabled(enabled);
    });
    this.remoteStreams.get(participantId)?.getVideoTracks().forEach((track) => {
      track.enabled = enabled;
    });
  }

  getParticipantStream(participantId: string): MediaStream | undefined {
    if (this.isBroadcastMode) {
      return participantId === SfuTransport.PROGRAM_ID ? this.remoteStream : undefined;
    }
    return this.remoteStreams.get(participantId);
  }

  private startKeepAlive(): void {
    if (this.statsPollInterval) return;
    const keepAliveMs = clampKeepAlive(this.deps.keepAliveMs);
    const tick = async () => {
      if (this.isBroadcastMode) await this.emitProgramTelemetry();
      else await this.emitRoomTelemetry();

      const ice = this.deps.ice;
      if (!ice.turnApiBase || !ice.turnToken || !ice.turnUsername) return;
      return this.liveness.tick(
        { turnApiBase: ice.turnApiBase, turnToken: ice.turnToken, turnUsername: ice.turnUsername },
        Date.now(),
      );
    };

    void tick();
    this.statsPollInterval = setInterval(tick, keepAliveMs);
  }

  /** Sample upload (publishPc) + program download (subscribePc) into one quality record. */
  private async emitProgramTelemetry(): Promise<void> {
    if (!this.deps.events) return;
    const now = Date.now();
    try {
      const pubStats = this.publishPc &&
        this.publishPc.signalingState !== 'closed' &&
        typeof (this.publishPc as any).getStats === 'function'
        ? await this.publishPc.getStats()
        : undefined;
      const pub = pubStats ? extractStatsSample(pubStats, now) : undefined;
      const subStats = this.subscribePc &&
        this.subscribePc.signalingState !== 'closed' &&
        typeof (this.subscribePc as any).getStats === 'function'
        ? await this.subscribePc.getStats()
        : undefined;
      const sub = subStats ? extractStatsSample(subStats, now) : undefined;
      if (!pub && !sub) return;

      const merged: StatsSample = {
        ts: now,
        bytesRecv: sub?.bytesRecv ?? 0,
        bytesSent: pub?.bytesSent ?? 0,
        packetsLost: sub?.packetsLost ?? 0,
        packetsReceived: sub?.packetsReceived ?? 0,
        rtt: Math.max(pub?.rtt ?? 0, sub?.rtt ?? 0),
        jitter: sub?.jitter ?? 0,
        availableOutgoingKbps: pub?.availableOutgoingKbps ?? 0,
      };
      const { details, bytesDelta } = computeQualityDetails(merged, this.programStats);
      this.programStats = merged;

      const iceState = (this.subscribePc?.iceConnectionState ?? this.publishPc?.iceConnectionState ?? 'new') as IceConnectionState;
      const subTransport = subStats ? extractPeerTransport(subStats) : undefined;
      let { score } = classifyQuality({ ...details, jitter: details.jitter, relayed: !!subTransport?.usingTurn });
      if (iceState !== 'connected' && iceState !== 'completed') score = 1;
      score = this.programSmoother.update(score);
      const level = qualityLevelForScore(score);

      this.deps.events.emit({ type: 'quality-changed', participantId: SfuTransport.PROGRAM_ID, score, level, details });
      this.deps.events.emit({
        type: 'peer-heartbeat',
        participantId: SfuTransport.PROGRAM_ID,
        heartbeat: {
          participantId: SfuTransport.PROGRAM_ID,
          iceConnectionState: iceState,
          connectionState: (this.subscribePc?.connectionState ?? this.publishPc?.connectionState ?? 'unknown') as string,
          transport: subTransport,
          quality: { score, level, details },
          alive: isAlive({ bytesDelta, iceConnectionState: iceState }),
          staleTicks: 0,
          ts: now,
        },
      });
    } catch (err) {
      console.warn('SfuTransport: program telemetry sample failed:', err);
    }
  }

  private async emitRoomTelemetry(): Promise<void> {
    if (!this.deps.events || !this.livekitRoom) return;
    const now = Date.now();
    try {
      const local = await this.collectLiveKitSample(this.livekitRoom.localParticipant.trackPublications.values(), now);
      const iceState = this.currentRoomIceState();

      for (const [participantId, participant] of this.remoteParticipants.entries()) {
        const remote = await this.collectLiveKitSample(participant.trackPublications.values(), now);
        const remoteSample = remote.sample;
        const localSample = local.sample;
        if (!remoteSample && !localSample) continue;

        const merged: StatsSample = {
          ts: now,
          bytesRecv: remoteSample?.bytesRecv ?? 0,
          bytesSent: localSample?.bytesSent ?? 0,
          packetsLost: remoteSample?.packetsLost ?? 0,
          packetsReceived: remoteSample?.packetsReceived ?? 0,
          rtt: Math.max(localSample?.rtt ?? 0, remoteSample?.rtt ?? 0),
          jitter: remoteSample?.jitter ?? 0,
          availableOutgoingKbps: localSample?.availableOutgoingKbps ?? 0,
        };
        const prev = this.roomStats.get(participantId);
        const { details, bytesDelta } = computeQualityDetails(merged, prev);
        this.roomStats.set(participantId, merged);

        const roomTransport = remote.transport ?? local.transport;
        let { score } = classifyQuality({ ...details, jitter: details.jitter, relayed: !!roomTransport?.usingTurn });
        if (iceState !== 'connected' && iceState !== 'completed') score = 1;
        let rSmoother = this.roomSmoothers.get(participantId);
        if (!rSmoother) { rSmoother = new QualitySmoother(); this.roomSmoothers.set(participantId, rSmoother); }
        score = rSmoother.update(score);
        const level = qualityLevelForScore(score);

        this.deps.events.emit({ type: 'quality-changed', participantId, score, level, details });

        const alive = isAlive({ bytesDelta, iceConnectionState: iceState });
        const stale = alive ? 0 : (this.roomStaleTicks.get(participantId) || 0) + 1;
        this.roomStaleTicks.set(participantId, stale);
        this.emitParticipantIceState(participantId, iceState);

        const heartbeat: PeerHeartbeat = {
          participantId,
          iceConnectionState: iceState,
          connectionState: String(this.livekitRoom.state),
          transport: remote.transport ?? local.transport,
          quality: { score, level, details },
          alive,
          staleTicks: stale,
          ts: now,
        };
        this.deps.events.emit({ type: 'peer-heartbeat', participantId, heartbeat });
      }
    } catch (err) {
      console.warn('SfuTransport: room telemetry sample failed:', err);
    }
  }

  async close(announceLeave = true): Promise<void> {
    if (this.statsPollInterval) {
      clearInterval(this.statsPollInterval);
      this.statsPollInterval = undefined;
    }

    if (this.livekitRoom) {
      await this.livekitRoom.disconnect(true).catch(() => undefined);
      this.livekitRoom = undefined;
    }

    await this.whip?.stop();
    await this.whep?.stop();
    this.whip = undefined;
    this.whep = undefined;
    this.publishPc?.close();
    this.subscribePc?.close();
    this.publishPc = undefined;
    this.subscribePc = undefined;
    this.currentRoom = undefined;
    this.programStats = undefined;
    this.roomStats.clear();
    this.roomStaleTicks.clear();
    this.remoteParticipants.clear();
    this.remoteStreams.clear();
    this.announcedParticipants.clear();
    this.participantIceStates.clear();
    this.localPublishedTracks.clear();

    this.remoteStream.getTracks().forEach((t) => {
      t.stop();
      this.remoteStream.removeTrack(t);
    });
    if (this.localStream) {
      this.localStream.getTracks().forEach((t) => t.stop());
      this.localStream = undefined;
    }
  }
}

/** The transport each provider drives. */
const PROVIDER_TRANSPORT: Record<SignalingProvider, 'p2p' | 'sfu'> = {
  'serverless-matchmaker': 'p2p',
  'whip-whep': 'sfu',
};

export interface ResolvedProvider {
  provider: SignalingProvider;
  transport: 'p2p' | 'sfu';
  /** True when the developer set it explicitly; false when inferred from the room. */
  explicit: boolean;
}

/**
 * Resolve the effective Signaling Infrastructure Provider for a room.
 *
 * - When the developer pins a provider, we honour it and *validate the room against
 *   it* — e.g. asking the Serverless Matchmaker to host >10 peers or a broadcast is
 *   a configuration error, so we fail loud with guidance instead of silently melting
 *   a 25-way mesh.
 * - When unset, we infer: any non-broadcast room that fits the mesh (≤10) → raw P2P
 *   matchmaker; everything larger or any broadcast → WHIP-WHEP. This preserves the
 *   SDK's original zero-config routing.
 */
export function resolveProvider(room: Room, explicit?: SignalingProvider): ResolvedProvider {
  if (explicit === 'serverless-matchmaker') {
    if (room.mode === 'broadcast') {
      throw new Error(
        `${PROVIDER_LABELS['serverless-matchmaker'].name} cannot host a broadcast — raw P2P has no egress. ` +
          `Use provider: 'whip-whep' (stream ingress & egress) for broadcast sessions.`,
      );
    }
    if (room.maxParticipants > MAX_MATCHMAKER_PARTICIPANTS) {
      throw new Error(
        `${PROVIDER_LABELS['serverless-matchmaker'].name} supports at most ${MAX_MATCHMAKER_PARTICIPANTS} participants ` +
          `(requested ${room.maxParticipants}); mesh egress scales O(N²). ` +
          `Use provider: 'whip-whep' for larger sessions.`,
      );
    }
    return { provider: 'serverless-matchmaker', transport: 'p2p', explicit: true };
  }

  if (explicit === 'whip-whep') {
    if (room.maxParticipants > MAX_SFU_PARTICIPANTS) {
      throw new Error(
        `${PROVIDER_LABELS['whip-whep'].name} supports at most ${MAX_SFU_PARTICIPANTS} participants ` +
          `(requested ${room.maxParticipants}).`,
      );
    }
    return { provider: 'whip-whep', transport: 'sfu', explicit: true };
  }

  // Inferred (back-compat): mesh-sized non-broadcast rooms stay P2P, the rest go SFU.
  const provider: SignalingProvider =
    room.mode !== 'broadcast' && room.maxParticipants <= MAX_MATCHMAKER_PARTICIPANTS
      ? 'serverless-matchmaker'
      : 'whip-whep';

  if (provider === 'whip-whep' && room.maxParticipants > MAX_SFU_PARTICIPANTS) {
    throw new Error(
      `${PROVIDER_LABELS['whip-whep'].name} supports at most ${MAX_SFU_PARTICIPANTS} participants ` +
        `(requested ${room.maxParticipants}).`,
    );
  }
  return { provider, transport: PROVIDER_TRANSPORT[provider], explicit: false };
}

/**
 * Pick the media transport for a room. Honours an explicit
 * `deps.provider` (validating the room against it) and otherwise infers one. The
 * resolution is announced on the event bus as `provider-selected` so UIs can show
 * which plane is active.
 */
export function selectTransport(room: Room, deps: TransportDeps): Transport {
  const resolved = resolveProvider(room, deps.provider);
  deps.events?.emit({
    type: 'provider-selected',
    provider: resolved.provider,
    transport: resolved.transport,
    explicit: resolved.explicit,
  });
  return resolved.transport === 'p2p' ? new P2PTransport(deps) : new SfuTransport(deps);
}

// ---------------------------------------------------------------------------
// Broadcast leg (WHIP ingest -> ZeroRate Kit Manager / LiveKit Egress -> WHEP)
// ---------------------------------------------------------------------------
export type BroadcastTarget =
  | { kind: 'rtmp'; url: string; streamKey: string }
  | { kind: 'srt'; url: string }
  | { kind: 'hls' }
  | { kind: 'rtsp' | 'zixi'; url: string }
  | { kind: 'youtube' | 'twitch' | 'facebook' | 'x'; streamKey: string };

/** Minimal HTTP surface WHIP/WHEP need — injectable so the exchange is unit-testable. */
export type HttpFetch = (
  url: string,
  init: any,
) => Promise<{ ok: boolean; status: number; headers: { get(name: string): string | null }; text(): Promise<string> }>;

/**
 * Best-effort wait for ICE gathering to finish so the offer carries its candidates
 * (WHIP/WHEP are non-trickle). Resolves immediately if already complete or if the
 * runtime/peer can't report gathering state; never blocks longer than `timeoutMs`.
 */
function waitForIceGathering(pc: RTCPeerConnection, timeoutMs = 1500): Promise<void> {
  if (pc.iceGatheringState === 'complete' || typeof pc.addEventListener !== 'function') return Promise.resolve();
  return new Promise<void>((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      pc.removeEventListener?.('icegatheringstatechange', check);
      resolve();
    };
    const check = () => {
      if (pc.iceGatheringState === 'complete') finish();
    };
    pc.addEventListener('icegatheringstatechange', check);
    setTimeout(finish, timeoutMs);
  });
}

/**
 * WebRTC-HTTP Ingestion Protocol client — publishes a local PeerConnection's tracks into
 * the Media Kit (stream ingress). POSTs the SDP offer, applies the returned answer, and
 * remembers the resource URL so the session can be torn down with DELETE.
 */
export class WhipClient {
  private resourceUrl?: string;
  private fetchImpl: HttpFetch;

  constructor(private endpoint: string, private token?: string, opts: { fetch?: HttpFetch } = {}) {
    this.fetchImpl = opts.fetch ?? ((globalThis as any).fetch as HttpFetch);
  }

  async publish(pc: RTCPeerConnection): Promise<void> {
    await pc.setLocalDescription(await pc.createOffer());
    await waitForIceGathering(pc);
    const sdp = pc.localDescription?.sdp ?? '';
    const res = await this.fetchImpl(this.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/sdp', ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}) },
      body: sdp,
    });
    if (!res.ok) throw new Error(`WHIP publish failed (${res.status}).`);
    const loc = res.headers.get('Location');
    if (loc) this.resourceUrl = new URL(loc, this.endpoint).toString();
    await pc.setRemoteDescription({ type: 'answer', sdp: await res.text() });
  }

  async stop(): Promise<void> {
    if (!this.resourceUrl) return;
    const url = this.resourceUrl;
    this.resourceUrl = undefined;
    try {
      await this.fetchImpl(url, { method: 'DELETE', headers: this.token ? { Authorization: `Bearer ${this.token}` } : {} });
    } catch {
      /* teardown is best-effort */
    }
  }
}

/**
 * WebRTC-HTTP Egress Protocol client — plays a remote stream from the Media Kit
 * (stream egress / low-latency viewer playback). Mirrors WHIP: POST offer, apply answer,
 * DELETE to stop. The caller adds recvonly transceivers and renders pc.ontrack output.
 */
export class WhepClient {
  private resourceUrl?: string;
  private fetchImpl: HttpFetch;

  constructor(private endpoint: string, private token?: string, opts: { fetch?: HttpFetch } = {}) {
    this.fetchImpl = opts.fetch ?? ((globalThis as any).fetch as HttpFetch);
  }

  async play(pc: RTCPeerConnection): Promise<void> {
    await pc.setLocalDescription(await pc.createOffer());
    await waitForIceGathering(pc);
    const sdp = pc.localDescription?.sdp ?? '';
    const res = await this.fetchImpl(this.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/sdp', ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}) },
      body: sdp,
    });
    if (!res.ok) throw new Error(`WHEP play failed (${res.status}).`);
    const loc = res.headers.get('Location');
    if (loc) this.resourceUrl = new URL(loc, this.endpoint).toString();
    await pc.setRemoteDescription({ type: 'answer', sdp: await res.text() });
  }

  async stop(): Promise<void> {
    if (!this.resourceUrl) return;
    const url = this.resourceUrl;
    this.resourceUrl = undefined;
    try {
      await this.fetchImpl(url, { method: 'DELETE', headers: this.token ? { Authorization: `Bearer ${this.token}` } : {} });
    } catch {
      /* teardown is best-effort */
    }
  }
}

/** Drives the broadcast: composite the layout, WHIP-ingest, fan out to each target. */
export class BroadcastController {
  constructor(private whip: WhipClient) {}

  async goLive(_targets: BroadcastTarget[]): Promise<void> {
    // Fail loudly rather than silently "succeeding": broadcast/multistream is a
    // Phase 2 feature (LiveKit Egress) and is not implemented in the MVP.
    throw new Error('Broadcast/multistream is not implemented in the MVP (Phase 2). See ARCHITECTURE.md.');
  }

  async stop(): Promise<void> {
    await this.whip.stop();
  }
}
